(function () {
  const DEFAULT_DURATION = 4500;
  let activeToast = null;
  let hideTimer = null;
  let offsetListenersBound = false;
  let offsetRaf = null;

  const CLOSE_ICON =
    '<svg width="9" height="9" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M12 2L2 12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/><path d="M12 12L2 2" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';

  function readCssPx(name, fallback) {
    const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    const value = parseFloat(raw);
    return Number.isFinite(value) ? value : fallback;
  }

  function syncToastOffset() {
    const root = document.getElementById('system-toast');
    if (!root) return;

    const headerGroup = document.getElementById('header-group');
    const gap = 8;
    let offset = gap;

    if (headerGroup) {
      const bottom = headerGroup.getBoundingClientRect().bottom;
      if (bottom > 0) {
        offset = bottom + gap;
      } else {
        const groupHeight = readCssPx('--header-group-height', 0);
        offset = (groupHeight > 0 ? groupHeight : readCssPx('--top-banner-height', 0) + readCssPx('--header-height', 56)) + gap;
      }
    } else {
      const groupHeight = readCssPx('--header-group-height', 0);
      offset = (groupHeight > 0 ? groupHeight : readCssPx('--top-banner-height', 0) + readCssPx('--header-height', 56)) + gap;
    }

    root.style.setProperty('--system-toast-offset-top', `${Math.round(offset)}px`);
  }

  function queueToastOffsetSync() {
    if (offsetRaf !== null) return;
    offsetRaf = window.requestAnimationFrame(() => {
      offsetRaf = null;
      syncToastOffset();
    });
  }

  function bindOffsetListeners() {
    if (offsetListenersBound) return;
    offsetListenersBound = true;
    window.addEventListener('scroll', queueToastOffsetSync, { passive: true });
    window.addEventListener('resize', queueToastOffsetSync, { passive: true });
    window.addEventListener('orientationchange', queueToastOffsetSync, { passive: true });
    document.addEventListener('shopify:section:load', queueToastOffsetSync);
  }

  function getRoot() {
    let root = document.getElementById('system-toast');
    if (!root) {
      root = document.createElement('div');
      root.id = 'system-toast';
      root.setAttribute('role', 'status');
      root.setAttribute('aria-live', 'polite');
      document.body.appendChild(root);
    }
    bindOffsetListeners();
    syncToastOffset();
    return root;
  }

  function clearHideTimer() {
    if (hideTimer) {
      clearTimeout(hideTimer);
      hideTimer = null;
    }
  }

  function hideSystemToast() {
    const root = document.getElementById('system-toast');
    if (!root) return;
    clearHideTimer();
    root.classList.remove('system-toast--visible');
    root.classList.add('system-toast--hiding');
    window.setTimeout(() => {
      root.classList.remove('system-toast--hiding');
      root.replaceChildren();
      activeToast = null;
    }, 220);
  }

  function normalizeImageUrl(url) {
    if (!url || typeof url !== 'string') return '';
    if (url.startsWith('//')) return `https:${url}`;
    return url;
  }

  function resizeShopifyImage(url, width) {
    const normalized = normalizeImageUrl(url);
    if (!normalized) return '';
    if (normalized.includes('cdn.shopify.com') || normalized.includes('shopifycdn.com')) {
      if (/[?&]width=\d+/.test(normalized)) {
        return normalized.replace(/([?&]width=)\d+/, `$1${width}`);
      }
      const separator = normalized.includes('?') ? '&' : '?';
      return `${normalized}${separator}width=${width}`;
    }
    return normalized;
  }

  function showSystemToast(options) {
    const config = options && typeof options === 'object' ? options : {};
    const title = config.title ? String(config.title) : '';
    if (!title) return;

    const root = getRoot();
    clearHideTimer();

    if (activeToast && typeof activeToast.onClose === 'function') {
      activeToast.onClose();
    }

    root.replaceChildren();
    root.onclick = null;
    root.classList.remove('system-toast--hiding');

    const card = document.createElement('div');
    card.className = 'system-toast__card';

    const container = document.createElement('div');
    container.className = 'system-toast__container';

    const media = document.createElement('div');
    media.className = 'system-toast__media';
    const imageUrl = resizeShopifyImage(config.image, 160);
    if (imageUrl) {
      const img = document.createElement('img');
      img.src = imageUrl;
      img.alt = config.imageAlt ? String(config.imageAlt) : '';
      img.width = 69;
      img.height = 69;
      img.loading = 'eager';
      img.decoding = 'async';
      media.appendChild(img);
    }

    const content = document.createElement('div');
    content.className = 'system-toast__content';

    const titleEl = document.createElement('p');
    titleEl.className = 'system-toast__title';
    titleEl.textContent = title;
    content.appendChild(titleEl);

    const subtitle = config.subtitle ? String(config.subtitle).trim() : '';
    if (subtitle) {
      const subtitleEl = document.createElement('p');
      subtitleEl.className = 'system-toast__subtitle';
      subtitleEl.textContent = subtitle;
      content.appendChild(subtitleEl);
    }

    const action = config.action && typeof config.action === 'object' ? config.action : null;
    let actionButton = null;
    if (action && action.label) {
      actionButton = document.createElement('button');
      actionButton.type = 'button';
      actionButton.className = 'system-toast__action';
      actionButton.textContent = String(action.label);
      content.appendChild(actionButton);
    }

    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'system-toast__close';
    close.setAttribute('aria-label', typeof Theme !== 'undefined' && Theme.translations?.toast?.close || 'Close');
    close.innerHTML = CLOSE_ICON;

    container.append(media, content);
    card.append(container, close);
    root.appendChild(card);

    const navigate = () => {
      if (typeof action.onClick === 'function') {
        action.onClick();
        return;
      }
      if (action.href) {
        window.location.href = action.href;
      }
    };

    if (actionButton) {
      actionButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (typeof config.onAction === 'function') {
          config.onAction(event);
        }
        navigate();
        hideSystemToast();
      });
    }

    if (action && (action.href || typeof action.onClick === 'function')) {
      content.classList.add('system-toast__content--clickable');
      content.addEventListener('click', (event) => {
        if (event.target.closest('.system-toast__action, .system-toast__close')) return;
        navigate();
        hideSystemToast();
      });
    }

    close.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      hideSystemToast();
    });

    activeToast = config;
    syncToastOffset();
    root.classList.add('system-toast--visible');

    const duration = Number.isFinite(config.duration) ? config.duration : DEFAULT_DURATION;
    if (duration > 0) {
      hideTimer = window.setTimeout(() => {
        hideSystemToast();
      }, duration);
    }
  }

  window.showSystemToast = showSystemToast;
  window.hideSystemToast = hideSystemToast;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', syncToastOffset, { once: true });
  } else {
    syncToastOffset();
  }
})();
